import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from '@drama/ui'
import { authoringErrorKey } from './authoring-error.js'
import { uploadLimitGuidance } from './upload-limit.js'

/** 与 contracts 的 NOVEL_ASSET_MIMES 一致。 */
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif'

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new globalThis.FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('FILE_READ_FAILED'))
    reader.readAsDataURL(file)
  })
}

/**
 * 选图上传的唯一流程（图片字段与素材面板共用）：读成 data URL 交给 `upload`（useNovelPack.upload），
 * 上传中状态与成功、失败提示都在这里；返回公开地址，失败或没选文件返回 null。
 * @param {{ upload: (file: { tag: string, file_name: string, mime: string, data_url: string }) => Promise<string>, tag: () => string }} opts
 */
export function useImageUpload({ upload, tag }) {
  const { t } = useI18n()
  const toast = useToast()
  const uploading = ref(false)
  const error_message = ref('')

  /** @param {Event & { target: HTMLInputElement }} event 文件框的 change 事件 */
  async function uploadPicked(event) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return null
    error_message.value = ''
    uploading.value = true
    try {
      const public_url = await upload({ tag: tag(), file_name: file.name, mime: file.type, data_url: await readFileAsDataUrl(file) })
      toast.success(t('novelStudio.fields.uploaded'))
      return public_url
    } catch (error) {
      const timed_out = error?.code === 'request_timeout' || error?.name === 'AbortError'
      const limit = uploadLimitGuidance(error)
      error_message.value = limit
        ? [t(limit.key), limit.retry_key ? t(limit.retry_key, limit.retry_params) : '', t('novelAuthoring.uploadLimits.preserved')].filter(Boolean).join(' ')
        : t(authoringErrorKey(error, timed_out ? 'novelStudio.fields.uploadTimeout' : 'novelStudio.fields.uploadFailed'))
      toast.error(error_message.value, { duration: 0 })
      return null
    } finally {
      uploading.value = false
    }
  }

  return { uploading, error_message, uploadPicked }
}
